<template>
  <div>
    <ds ds="input-file"
      v-if="elementProps"
      :isDisabled="isDisabled || elementProps.isDisabled"
      :isReadonly="elementProps.isReadonly"
      :isUploading="isLoading && readyToUpload"
      :progress="progress"
      :name="elementProps.key"
      :label="this.shadowValue && this.shadowValue.url ? $t('view.documents.uploaded') : $t('view.documents.selected')"
      :helperText="!isLoading || !readyToUpload ? elementProps.helperText : $t('shared.message.pleaseWait')"
      :helperTextTitle="!isLoading || !readyToUpload ? elementProps.helperTextTitle : $t('shared.message.processing')"
      :mimetype="elementProps.mimeTypes"
      :maxsize="elementProps.maxsize || elementProps.maxSize"
      :isRequired="elementProps.isRequired"
      ref="dsInputFile"
      :value="shadowValue"
      @dsInput="e => (fileValue = e.detail)"
      @upload="e => (handleUpload({e, documentId}))"
      @uploadError="handleUploadError"
      @delete="handleDelete"
    />
  </div>
</template>
<script>

import { mapGetters, mapActions } from 'vuex';
import BMF from 'browser-md5-file';
import { LoggerService } from '@/services';
import { eventBus } from '@/eventBus';

export default {
  name: 'FileUploadElement',
  props: ['element', 'readonly', 'fileValues', 'fieldsetId', 'nowTime', 'dowloadQueue'],
  data: () => ({
    retryInterval: 5, // fetchDownload polling interval, in seconds
    retries: 12, // fetchDownload polling number of attempts
    expiryOffset: 15, // how many secondss before url expiry to fetchDownload
    elementProps: null,
    fileValue: null,
    fileUrl: null,
    fileVersion: null,
    fileExpiryDate: null,
    isValid: null,
    retryAttempt: 0,
    retryTime: null,
    gotLink: false,
    progress: 1 // upload progress, guestimate
  }),
  computed: {
    ...mapGetters({
      documentUrlByFieldsetId: 'qualification/documentUrlByFieldsetId',
      documentLinkByDocumentId: 'qualification/documentLinkByDocumentId'
    }),
    readyToUpload () { // have all files been selected by user (for multi-file fieldsets)
      return this.fileValue instanceof File && Object.keys(this.fileValues[this.fieldsetId]).reduce((acc, key) =>
        (!!(acc && (!this.fileValues[this.fieldsetId][key].url && this.fileValues[this.fieldsetId][key].filename))), true);
    },
    readyToRetry () { // has polling interval elaspsed for fetchDownload
      return this.retryTime && this.nowTime >= this.retryTime;
    },
    isExpired () {
      return this.fileExpiryDate && this.fileExpiryDate - this.nowTime <= this.expiryOffset * 1000;
    },
    readyToDownload () {
      return this.isExpired || (this.readyToRetry && this.topOfQueue);
    },
    isDisabled () {
      return this.element.disableWhen ? Object.keys(this.element.disableWhen).reduce((acc, key) =>
        acc && this.elementValues[key] === this.element.disableWhen[key], true) : false;
    },
    shadowValue () {
      if (!this.fileValue) return null;
      return {
        name: this.fileValue.name,
        size: this.fileValue.size,
        lastModified: this.fileValue.lastModified,
        type: this.fileValue.type,
        url: this.fileUrl || this.fileValue.url
      };
    },
    isLoading () {
      return this.isValid !== false && !!(!this.fileUrl && this.fileValue);
    },
    documentId () {
      return this.fileValues.length === 1 ? this.elementProps.key : `${this.fieldsetId}__${this.elementProps.key}`;
    },
    topOfQueue () {
      return this.dowloadQueue.indexOf(this.documentId) === this.dowloadQueue.length - 1;
    }
  },
  watch: {
    isLoading: {
      async handler (state, prevState) {
        if (this.documentId && state !== prevState) eventBus.$emit('fileIsLoading', { documentId: this.documentId, loading: state, required: this.elementProps.required });
      }
    },
    async readyToDownload (ready) {
      if (ready) {
        this.fetchDownload();
      }
    }
  },
  methods: {
    ...mapActions({
      fetchDocumentUrl: 'qualification/fetchDocumentUrl',
      fetchDocumentLink: 'qualification/fetchDocumentLink',
      removeDocumentLink: 'qualification/removeDocumentLink',
      removeDocumentUrl: 'qualification/removeDocumentUrl'
    }),
    buildElementProps: function (element) {
      return {
        key: element.name || element.id,
        isDisabled: false,
        isRequired: element.required,
        isReadonly: this.readonly,
        ...element
      };
    },
    addToQueue: function () { // add to bottom of queue
      this.removeFromQueue();
      this.dowloadQueue.unshift(this.documentId);
    },
    removeFromQueue: function () { // remove if exists in queue
      if (this.dowloadQueue.includes(this.documentId)) {
        this.dowloadQueue.splice(this.dowloadQueue.indexOf(this.documentId), 1);
      }
    },

    // poll download endpoint every x secs up to y times (see retryTime, retryInterval in Data())
    fetchDownload: async function () {
      try {
        this.gotLink = false;
        this.progress = 70;
        await this.fetchDocumentLink({ documentId: this.documentId });
        const link = this.documentLinkByDocumentId({ documentId: this.documentId });
        this.progress = 80;

        if (this.retryAttempt === 0 || (this.readyToRetry && this.retryAttempt < this.retries)) {
          this.progress += 2;
          if (this.isExpired || (this.readyToUpload && this.fileVersion !== link.version)) {
            this.fileUrl = link.url; // sets isLoading computed to false
            this.fileVersion = link.version;
            this.fileExpiryDate = Number(link.expiryDate);
            this.gotLink = true;
            this.retryTime = null;
            this.retryAttempt = 0;
            this.removeFromQueue();
          }
        }
        if (this.retryAttempt >= this.retries) {
          this.removeFromQueue();
          this.clearInput();
          throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
        }
      } catch (error) {
        // if there is no existing file for this ONCHAINID, this is an expected error code
        if (!error.code || error.code !== 'ERROR_DOCUMENTS_NOT_FOUND') {
          this.removeFromQueue();
          LoggerService.error(error);
        }
        this.removeDocumentLink({ documentId: this.documentId });
      }
      if (!this.gotLink) {
        this.retryAttempt++;
        this.retryTime = this.nowTime + this.retryInterval * 1000;
      }
    },
    handleUpload: async function ({ e, documentId }) {
      this.isValid = true;
      this.fileExpiryDate = null;
      this.retryAttempt = 0;
      this.retryTime = null;

      if (this.elementProps.type === 'input-file' && e.detail instanceof File && documentId === this.documentId) {
        this.fileUrl = null;
        const file = e.detail;

        const bmf = new BMF(); // browser-md5-file instance

        try {
          const fileMd5 = new Promise((resolve, reject) => {
            bmf.md5(file, (err, md5) => {
              if (err) return reject(err);
              resolve(md5);
            });
          });

          file.md5 = await fileMd5;

          const fileInfo = {
            id: this.elementProps.key,
            label: this.elementProps.key,
            filename: file.name,
            type: file.type,
            md5: file.md5,
            size: file.size,
            file
          };
          this.fileValues[this.fieldsetId][this.elementProps.key] = fileInfo;

          this.addToQueue();

          if (this.readyToUpload) {
            this.progress = 1;
            const files = Object.keys(this.fileValues[this.fieldsetId]).map(k => {
              // remove file prop before POSTing
              const { file: removed, ...uploadFileInfo } = this.fileValues[this.fieldsetId][k];
              return uploadFileInfo;
            });

            await this.fetchDocumentUrl({ fieldsetId: this.fieldsetId, files });
            this.progress = 10;
            const s3Url = this.documentUrlByFieldsetId({ fieldsetId: this.fieldsetId });
            this.progress = 20;

            if (!s3Url ||
              !s3Url[this.fieldsetId] ||
              !s3Url[this.fieldsetId].filesToUpload ||
              !Array.isArray(s3Url[this.fieldsetId].filesToUpload) ||
              !s3Url[this.fieldsetId].filesToUpload.length
            ) {
              throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
            }

            const s3Responses = await Promise.all(s3Url[this.fieldsetId].filesToUpload.map((fl) => {
              if (!fl.href || !fl.href.url || !fl.href['form-data']) {
                throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
              }

              const s3Path = fl.href.url;

              // find the matching fileValueKey by comparing md5 (need to convert base 16 to base 64 first)
              const fileValueKey = Object.keys(this.fileValues[this.fieldsetId]).find(fv => {
                const md5 = (source => btoa(
                  String.fromCharCode(
                    ...source
                      .match(/.{2}/g)
                      .map(c => parseInt(c, 16))
                  )
                ))(this.fileValues[this.fieldsetId][fv].md5);

                return md5 === fl.contentMd5;
              });

              const fileValue = this.fileValues[this.fieldsetId][fileValueKey];
              const fileBlob = new Blob([fileValue.file], { type: fileValue.type });

              this.progress = 40;

              return fetch(s3Path, {
                method: 'PUT',
                body: fileBlob
              });
            }));

            if (!s3Responses.length) {
              throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
            }
            if (this.fileValue) {
              this.fileValue.filename = null;
            }
            eventBus.$emit('fileUploaded', { fieldsetId: this.fieldsetId });
          }
        } catch (error) {
          this.removeFromQueue();
          this.removeDocumentUrl({ fieldsetId: this.fieldsetId });
          this.fileValue = null;
          LoggerService.error(error);
        }
      }
    },
    handleUploadError: async function (e) {
      this.isValid = false;
      throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
    },

    handleDelete: async function (e) {
      this.clearInput();
    },
    handleFileUploaded: async function ({ fieldsetId }) {
      if (fieldsetId === this.fieldsetId) {
        this.fetchDownload();
      }
    },
    clearInput: function () {
      this.retryTime = null;
      this.retryAttempt = 0;
      this.fileValue = null;
    }
  },
  async mounted () {
    // load existing file props
    const fileValue = this.fileValues[this.fieldsetId][this.element.id].url ? this.fileValues[this.fieldsetId][this.element.id] : null;
    if (fileValue) {
      const existingFile = {
        name: fileValue.filename,
        size: fileValue.size,
        lastModified: fileValue.lastModified,
        type: fileValue.type,
        url: fileValue.url,
        version: fileValue.version
      };

      this.fileValue = existingFile;
      this.fileUrl = existingFile.url;
      this.fileVersion = existingFile.version;
      this.fileExpiryDate = fileValue.expiryDate ? Number(fileValue.expiryDate) : null;
    }

    this.elementProps = this.buildElementProps(this.element);

    // register event listeners
    eventBus.$on('fileUploaded', this.handleFileUploaded);
  },
  beforeUnmount () {
    this.removeFromQueue();
  }
};
</script>
